import leargasClover from 'assets/img/leargas-clover.png';
import Flex from 'components/common/Flex';
import DisplaySettingsPanel from 'components/common/settings-panel/DisplaySettingsPanel';
import AccountProvider from 'context/AccountProvider';
import ApplicationProvider from 'context/ApplicationProvider';
import AuthenticationProvider from 'context/AuthenticationProvider';
import { AxiosProvider } from 'context/AxiosProvider';
import ESGlobalFiltersProvider from 'context/ESGlobalFiltersProvider';
import GlobalLoaderProvider from 'context/GlobalLoaderProvider';
import { UnsavedChangesProvider } from 'context/UnsavedChangesProvider';
import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { Image } from 'react-bootstrap';
import CookieConsent from 'react-cookie-consent';

// Create context boundaries to optimize re-renders
const AccountBoundary = memo(({ children }) => {
  return <AccountProvider>{children}</AccountProvider>;
});

AccountBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

const FiltersBoundary = memo(({ children }) => {
  return <ESGlobalFiltersProvider>{children}</ESGlobalFiltersProvider>;
});

FiltersBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

const UnsavedChangesBoundary = memo(({ children }) => {
  return <UnsavedChangesProvider>{children}</UnsavedChangesProvider>;
});

UnsavedChangesBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * MainProvider component with optimized context hierarchy.
 *
 * @component
 * @param {Object} props - The component props
 * @param {ReactNode} props.children - The child components
 * @returns {ReactNode} The rendered component
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @since 0.1.0-beta.1
 * @version 0.1.0-beta.7
 */
const MainProvider = ({ children }) => {
  // Prefer env-driven cookie settings where available
  const consentCookieName =
    process.env.REACT_APP_CONSENT_COOKIE_NAME || 'consent';
  const cookieDomain = process.env.REACT_APP_SERVICE_SECURE_COOKIE_DOMAIN;
  const cookieSecure =
    String(process.env.REACT_APP_COOKIE_SECURE || 'true')
      .toLowerCase()
      .trim() !== 'false';
  const cookieSameSite = 'None';
  return (
    <ApplicationProvider>
      {/**
       * DisplaySettingsPanel component
       * This component is used to show display settings for the application to the user.
       */}
      <DisplaySettingsPanel />
      <AuthenticationProvider>
        <GlobalLoaderProvider>
          <AxiosProvider>
            <AccountBoundary>
              <UnsavedChangesBoundary>
                <FiltersBoundary>{children}</FiltersBoundary>
              </UnsavedChangesBoundary>
            </AccountBoundary>
          </AxiosProvider>
        </GlobalLoaderProvider>
      </AuthenticationProvider>
      <CookieConsent
        location='bottom'
        buttonText='I understand'
        cookieName={consentCookieName}
        cookieDomain={cookieDomain}
        cookieSecure={cookieSecure}
        cookieSameSite={cookieSameSite}
        style={{
          background: 'transparent',
          padding: 0,
          zIndex: '999999'
        }}
        containerClasses='bg-dark'
        contentStyle={{ margin: 0 }}
        buttonWrapperClasses='p-4 m-0'
        buttonClasses='btn btn-sm bg-warning m-0'
        expires={365}>
        <Flex alignItems='center' className='bg-dark p-4'>
          <span>
            <Image
              src={leargasClover}
              width='50'
              height='50'
              className='me-2'
            />
          </span>
          <span className='fs--1 text-100'>
            <strong>
              Léargas uses cookies on this website to customize and enhance your
              experience.
            </strong>
            <br />
            By continuing, you agree to the use of these cookies. For more
            information about how we handle and protect your personal data,
            please read our &nbsp;
            <a
              href='https://www.leargassecurity.com/privacy-leargas-security/'
              rel='noreferrer'
              target='_blank'>
              Privacy Policy
            </a>
            .
          </span>
        </Flex>
      </CookieConsent>
    </ApplicationProvider>
  );
};

MainProvider.propTypes = {
  children: PropTypes.node
};

export default MainProvider;
